import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { PluginHealthResponse } from '@reflex/plugin-api';

/**
 * Polling interval for plugin health queries. This creates periodic HTTP
 * requests while the plugin detail page is open. Trade-off: keeps liveness
 * data fresh without requiring WebSocket infrastructure, at the cost of
 * additional HTTP traffic. Consider WebSocket push if health page usage
 * grows significantly.
 */
const POLL_INTERVAL_MS = 20_000;

/**
 * Query hook for fetching the health status of a single plugin.
 * Polls every 20s while the component is visible to keep liveness data fresh.
 */
export function usePluginHealth(name: string) {
  return useQuery({
    queryKey: queryKeys.pluginHealth(name),
    queryFn: () => api.getPluginHealth(name),
    refetchInterval: POLL_INTERVAL_MS,
  });
}

/**
 * Mutation hook for triggering a dependency health check on a plugin.
 * Updates the plugin health query cache on success.
 */
export function useRunPluginDependencyCheck(name: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.runPluginDependencyCheck(name),
    onSuccess: (dependencyResult) => {
      queryClient.setQueryData<PluginHealthResponse>(queryKeys.pluginHealth(name), (old) => {
        if (!old) {
          return {
            name,
            startup: null,
            liveness: null,
            dependencies: dependencyResult,
          };
        }
        return {
          ...old,
          dependencies: dependencyResult,
        };
      });
    },
  });
}
